import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import ClockIcon from "@redotech/redo-icons/clock.svg";
import {
  ShopifyFulfillmentHoldDisplayNames,
  ShopifyFulfillmentHoldReason,
} from "@redotech/redo-model/fulfillments/shopify-fulfillment-schemas";
import { toast } from "@redotech/redo-web/alert";
import { DeprecatedDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-date-picker";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import {
  RedoModal,
  RedoModalProps,
} from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";

const displayReasons: ShopifyFulfillmentHoldReason[] = [
  ShopifyFulfillmentHoldReason.InventoryOutOfStock,
  ShopifyFulfillmentHoldReason.IncorrectAddress,
  ShopifyFulfillmentHoldReason.AwaitingPayment,
  ShopifyFulfillmentHoldReason.HighRiskOfFraud,
  ShopifyFulfillmentHoldReason.AwaitingReturnItems,
  ShopifyFulfillmentHoldReason.OnlineStorePostPurchaseCrossSell,
  ShopifyFulfillmentHoldReason.UnknownDeliveryDate,
  ShopifyFulfillmentHoldReason.Other,
];

export const PlaceOnHoldModal = memo(function PlaceOnHoldModal({
  closeModal,
  fulfillmentGroupIds,
  isOpen,
}: {
  closeModal: () => void;
  fulfillmentGroupIds: string[] | undefined;
  isOpen: boolean;
}) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);

  const [holdUntil, setHoldUntil] = useState<Date | undefined>(undefined);
  const [reason, setReason] = useState<string>("");
  const [reasonOption, setReasonOption] =
    useState<ShopifyFulfillmentHoldReason>(ShopifyFulfillmentHoldReason.Other);

  const [loading, setLoading] = useState(false);
  const handleSubmit = useHandler(async () => {
    setLoading(true);
    if (!fulfillmentGroupIds) {
      return;
    }
    const { failedGroupIds } = await client.placeHolds({
      fulfillmentGroupIds,
      holdUntil,
      reason,
      reasonOption,
    });
    setLoading(false);
    if (failedGroupIds.length > 0) {
      toast(
        `Failed to place on hold for orders: ${failedGroupIds.join(", ")}`,
        { variant: "error", duration: Temporal.Duration.from({ seconds: 2 }) },
      );
    } else {
      toast(`Holds placed on ${fulfillmentGroupIds.length} orders`, {
        variant: "success",
        duration: Temporal.Duration.from({ seconds: 2 }),
      });
    }
    closeModal();
  });

  const modalProps: RedoModalProps = {
    TitleIcon: ClockIcon,
    theme: "neutral",
    title: "Hold fulfillment",
    modalSize: "sm",
    isOpen,
    onModalCloseRequested: closeModal,
    primaryButton: {
      text: "Place on hold",
      onClick: handleSubmit,
      disabled: loading || !fulfillmentGroupIds,
    },
    secondaryButton: { text: "Cancel", onClick: closeModal },
  };

  return (
    <RedoModal {...modalProps}>
      <Flex dir="column" gap="xl">
        <Flex align="center" gap="lg">
          <Text fontSize="sm" fontWeight="medium">
            Hold fulfillment until
          </Text>
          <DeprecatedDatePicker
            date={holdUntil}
            minDate={new Date()}
            setDate={setHoldUntil}
          />
        </Flex>
        <HoldReasonDropdown
          reasonOption={reasonOption}
          setReasonOption={setReasonOption}
        />
        <RedoTextareaInputField
          label="Reason"
          placeholder="Enter reason"
          setValue={setReason}
          value={reason}
        />
      </Flex>
    </RedoModal>
  );
});

export const HoldReasonDropdown = memo(function HoldReasonDropdown({
  reasonOption,
  setReasonOption,
}: {
  reasonOption: ShopifyFulfillmentHoldReason;
  setReasonOption: (reason: ShopifyFulfillmentHoldReason) => void;
}) {
  return (
    <RedoSingleSelectDropdownInput
      label="On hold reason"
      options={displayReasons.map((reason) => ({
        type: "text",
        text: ShopifyFulfillmentHoldDisplayNames[reason],
        id: reason,
        value: reason,
      }))}
      optionSelected={(args) => {
        setReasonOption(args.item.value);
      }}
      placeholder="Select reason"
      selectedItem={{
        type: "text",
        text: ShopifyFulfillmentHoldDisplayNames[reasonOption],
        id: reasonOption,
        value: reasonOption,
      }}
    />
  );
});
